import type { ReactNode } from 'react';
import Link from '@/lib/nav/link';
import { Logo } from '@/components/branding/Logo';
import { cn } from '@/lib/utils';

/**
 * The "never leave anyone stranded" error shell. One presentational component
 * shared by every error surface in the app — root `error.tsx`, 404, trip-level
 * boundaries, failed Convex query fallbacks, future suspense errors.
 *
 * Keep the visual language identical wherever it shows up so the user always
 * knows: "this is a Roam hiccup, here's how to get back on track." Callers
 * supply their own eyebrow, copy, and the small set of escape-hatch actions
 * (try again, go home, current trip, etc.) appropriate for the surface.
 *
 * Design rules baked in:
 * - Logo + ivory backdrop = "this is still Roam, you didn't end up on a
 *   browser error page."
 * - At least one action is always primary so there's a clear next step.
 * - `fullScreen` defaults true so the component owns the viewport, but can be
 *   set false to drop the shell inside an existing layout (e.g. inside the
 *   trip workspace where the top nav should stay visible).
 */

export interface FriendlyErrorAction {
  label: string;
  /** External or internal link target. Mutually exclusive with onClick. */
  href?: string;
  /** Client callback (e.g. error reset). Mutually exclusive with href. */
  onClick?: () => void;
  /** Default 'secondary'. Use 'primary' for the recommended next step. */
  variant?: 'primary' | 'secondary';
}

export interface FriendlyErrorProps {
  /** Short uppercase tag — "Support", "404", "Offline". Defaults to "Heads up". */
  eyebrow?: string;
  /** Main heading. Defaults to the canonical "Something went sideways." */
  title?: string;
  /** Body copy. Required — say what happened and where they can go. */
  description: ReactNode;
  /** Buttons + links to escape the dead-end. Render order is preserved. */
  actions?: FriendlyErrorAction[];
  /**
   * When true (default), fills the viewport so the shell is the whole page.
   * Pass false to drop the card inline (e.g. inside a trip workspace pane).
   */
  fullScreen?: boolean;
  className?: string;
}

export function FriendlyError({
  eyebrow = 'Heads up',
  title = 'Something went sideways.',
  description,
  actions,
  fullScreen = true,
  className,
}: FriendlyErrorProps) {
  return (
    <div
      className={cn(
        'grid place-items-center bg-ivory px-6 text-center',
        fullScreen ? 'min-h-screen' : 'min-h-[60vh] py-12',
        className,
      )}
    >
      <section className="max-w-lg rounded-lg border border-black/10 bg-white p-6 shadow-sm">
        <Logo variant="with-clouds" width={66} height={55} className="mx-auto" />
        <p className="mt-4 text-xs font-black uppercase tracking-wide text-primary">{eyebrow}</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-ink">{title}</h1>
        <div className="mt-3 text-sm leading-6 text-ink/70">{description}</div>
        {actions && actions.length > 0 ? (
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            {actions.map((action, index) => (
              <FriendlyErrorActionButton key={`${action.label}-${index}`} action={action} />
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function FriendlyErrorActionButton({ action }: { action: FriendlyErrorAction }) {
  const variant = action.variant ?? 'secondary';
  const className =
    variant === 'primary'
      ? 'rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground transition hover:bg-primary/90'
      : 'rounded-md border border-input bg-white px-4 py-2 text-sm font-semibold text-ink transition hover:bg-muted';

  if (action.href) {
    return (
      <Link href={action.href} className={className}>
        {action.label}
      </Link>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={className}>
      {action.label}
    </button>
  );
}
